// Front-end mirror of functions/utils/smart-upload/lineRequirements.js -- the
// single rule set the server commit gate and this review UI both apply.
// functions/__tests__/utils/smart-upload/unit/test_lineRequirementsParity.js
// reads this file's text and fails if any list below drifts from the server
// module, so every list stays a plain JSON-literal array of strings.

export const REVIEW_STATES = Object.freeze({
  pendingReview: "pending_review",
  committing: "committing",
  committed: "committed",
  rejected: "rejected",
  error: "error",
  manualFallback: "manual_fallback",
  duplicateSkipped: "duplicate_skipped"
});

export const LINE_ORIGINS = Object.freeze({
  extracted: "extracted",
  manualFallback: "manual_fallback"
});

export const TERMINAL_REVIEW_STATES_LIST = [
  "committed",
  "rejected",
  "duplicate_skipped",
  "manual_fallback"
];

export const PHYSICALLY_LOCATED_FAMILIES_LIST = [
  "utility_bill",
  "fuel_receipt"
];

export const PHYSICALLY_LOCATED_SUBCATEGORIES_LIST = [
  "electricity",
  "gas",
  "company-vehicles",
  "purchased-water",
  "steam",
  "chill-water",
  "equipment-fuel"
];

export const VEHICLE_SUBCATEGORIES_LIST = ["company-vehicles"];

export const SPEND_SUBCATEGORIES_LIST = [
  "spend-based",
  "purchased-goods-services",
  "capital-goods",
  "upstream-transport-distribution",
  "downstream-transportation-and-distribution",
  "waste-generated-in-operations",
  "business-travel",
  "employee-commuting",
  "upstream-leased-assets"
];

export const CATEGORY_FACTOR_SUBCATEGORIES_LIST = [
  "flights",
  "hotels",
  "rides",
  "trains",
  "rental-cars",
  "rented-equipment",
  "shipping",
  "waste",
  "purchased-water",
  "equipment-fuel"
];

export const UTILITY_SUBCATEGORIES_LIST = [
  "electricity",
  "gas",
  "utilities",
  "steam",
  "chill-water"
];

// #372's migrated CSV families reviewed line by line (ticket
// fuVH2FrxRiQuKqF2n9uF): no entity, no taxonomy pick, and a transactions line
// always needs a vendor. bom_products_csv joined this set in ticket
// DlAT2n1ITtjB6BA9ntuJ -- its vendor is enforced at draft time instead
// (bom_new_supplier attention reason), so requiresVendor stays false for it.
// utilities_csv/office_csv (ticket #412): the office is fixed by the task's
// own entity_context, not picked by a reviewer, so they join this set too.
export const REVIEW_DRIVEN_CSV_FAMILIES_LIST = [
  "transactions_csv",
  "shipping_transactions",
  "bom_products_csv",
  "utilities_csv",
  "office_csv",
  "fuel_csv",
  "flights_csv"
];

export const CURRENCY_UNITS_LIST = [
  "usd",
  "$",
  "dollars",
  "dollar",
  "cad",
  "eur",
  "gbp",
  "aud"
];

export const TERMINAL_REVIEW_STATES = new Set(TERMINAL_REVIEW_STATES_LIST);
export const PHYSICALLY_LOCATED_FAMILIES = new Set(
  PHYSICALLY_LOCATED_FAMILIES_LIST
);
export const PHYSICALLY_LOCATED_SUBCATEGORIES = new Set(
  PHYSICALLY_LOCATED_SUBCATEGORIES_LIST
);
export const VEHICLE_SUBCATEGORIES = new Set(VEHICLE_SUBCATEGORIES_LIST);
export const SPEND_SUBCATEGORIES = new Set(SPEND_SUBCATEGORIES_LIST);
export const CATEGORY_FACTOR_SUBCATEGORIES = new Set(
  CATEGORY_FACTOR_SUBCATEGORIES_LIST
);
export const UTILITY_SUBCATEGORIES = new Set(UTILITY_SUBCATEGORIES_LIST);
export const REVIEW_DRIVEN_CSV_FAMILIES = new Set(
  REVIEW_DRIVEN_CSV_FAMILIES_LIST
);
export const CURRENCY_UNITS = new Set(CURRENCY_UNITS_LIST);

export const isReviewDrivenCsvFamily = (documentFamily) =>
  REVIEW_DRIVEN_CSV_FAMILIES.has(documentFamily);

export const isCurrencyUnit = (unit) =>
  typeof unit === "string" && CURRENCY_UNITS.has(unit.trim().toLowerCase());

export const requiresEntity = ({ documentFamily, subcategory }) =>
  !isReviewDrivenCsvFamily(documentFamily) &&
  (PHYSICALLY_LOCATED_FAMILIES.has(documentFamily) ||
    PHYSICALLY_LOCATED_SUBCATEGORIES.has(subcategory));

export const requiresVendor = ({ documentFamily, unit, subcategory }) => {
  if (isReviewDrivenCsvFamily(documentFamily)) {
    return documentFamily === "transactions_csv";
  }
  return isCurrencyUnit(unit) && SPEND_SUBCATEGORIES.has(subcategory);
};

export const requiresTaxonomy = ({ documentFamily }) =>
  !isReviewDrivenCsvFamily(documentFamily);

// Always derived from the line, never from a slideout's entity_context: an
// office-slideout fuel receipt still needs a vehicle. Once a line's category
// is resolved to a non-vehicle subcategory (e.g. equipment-fuel for a
// propane tank fill), that overrides the fuel-receipt family default so a
// stationary-equipment fuel receipt is not forced into a vehicle picker.
export const entityTypeForLine = ({ documentFamily, subcategory }) => {
  if (subcategory) {
    return VEHICLE_SUBCATEGORIES.has(subcategory) ? "vehicles" : "offices";
  }
  return documentFamily === "fuel_receipt" ? "vehicles" : "offices";
};
